import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useState, Fragment } from 'react'

interface Client {
  id: string
  firstName?: string
  lastName?: string
  companyName?: string
  type: 'individual' | 'company'
  email?: string
  phone?: string
  cuit?: string
  dni?: string
}

interface ClientComboboxProps {
  clients: Client[]
  selectedClient: Client | null
  onSelect: (client: Client | null) => void
  onCreateClient?: (query: string) => void
  placeholder?: string
  getClientName: (client: Client) => string
}

export default function ClientCombobox({
  clients,
  selectedClient,
  onSelect,
  onCreateClient,
  placeholder = 'Buscar cliente...',
  getClientName
}: ClientComboboxProps) {
  const [query, setQuery] = useState('')

  const filteredClients = (() => {
    if (query === '') {
      // Sin query: orden alfabético por nombre, con "Consumidor Final" fijado primero
      const sorted = clients.slice().sort((a, b) => {
        const na = getClientName(a).toLowerCase()
        const nb = getClientName(b).toLowerCase()
        return na.localeCompare(nb, 'es', { sensitivity: 'base' })
      })
      const cfIdx = sorted.findIndex((c) => c.id === 'consumidor-final')
      if (cfIdx > 0) {
        const [cf] = sorted.splice(cfIdx, 1)
        sorted.unshift(cf)
      }
      return sorted
    }
    const searchQuery = query.toLowerCase()
    const searchDigits = searchQuery.replace(/\D/g, '')
    // Score: 0 = nombre empieza, 1 = nombre contiene, 2 = email/phone/doc, 3 = no match
    const scored = clients.map((client) => {
      try {
        const clientName = getClientName(client).toLowerCase()
        const email = (client.email || '').toLowerCase()
        const phone = (client.phone || '').toLowerCase()
        const cuitDigits = String((client as any).cuit || '').replace(/\D/g, '')
        const dniDigits = String((client as any).dni || '').replace(/\D/g, '')
        let score = 3
        if (clientName.startsWith(searchQuery)) score = 0
        else if (clientName.includes(searchQuery)) score = 1
        else if (email.includes(searchQuery) || phone.includes(searchQuery)) score = 2
        else if (searchDigits.length >= 3 && (cuitDigits.includes(searchDigits) || dniDigits.includes(searchDigits))) score = 2
        return { client, score, name: clientName }
      } catch {
        return { client, score: 3, name: '' }
      }
    })
    return scored
      .filter((s) => s.score < 3)
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score
        return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      })
      .map((s) => s.client)
  })()

  const trimmedQuery = query.trim()
  const showCreateOption = !!onCreateClient && trimmedQuery.length > 0
  // ¿Lo tipeado es un teléfono? (solo dígitos y separadores, 7-15 dígitos)
  const queryDigits = trimmedQuery.replace(/\D/g, '')
  const queryIsPhone = /^[\d\s()+.\-]+$/.test(trimmedQuery) && queryDigits.length >= 7 && queryDigits.length <= 15

  const handleChange = (client: Client | null) => {
    if (client?.id === '__create__') {
      onCreateClient?.(query.trim())
      return
    }
    onSelect(client)
  }

  return (
    <Combobox value={selectedClient} onChange={handleChange}>
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className="w-full h-auto px-4 py-3 pl-10 pr-10 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-sm"
            placeholder={placeholder}
            displayValue={(client: Client | null) => (client ? getClientName(client) : '')}
            onChange={(event) => {
              setQuery(event.target.value)
            }}
          />

          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <Combobox.Button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg shadow-lg border bg-card">
          {filteredClients.length === 0 && !showCreateOption && trimmedQuery !== '' && (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <p className="font-medium mb-1">No se encontraron clientes</p>
              <p className="text-sm">Intenta buscar por nombre, email o telefono</p>
            </div>
          )}

          {filteredClients.map((client) => {
            let clientName = 'Sin nombre'
            try {
              clientName = getClientName(client)
            } catch (error) {
              console.error('Error al obtener nombre del cliente:', error)
            }
            return (
              <Combobox.Option key={client.id} value={client} as={Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative cursor-pointer select-none px-4 py-2 transition-colors ${
                      active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${selected ? 'font-semibold' : 'font-normal'}`}>{clientName}</span>
                      {selected && <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />}
                    </div>
                  </li>
                )}
              </Combobox.Option>
            )
          })}

          {showCreateOption && (
            <Combobox.Option
              value={{ id: '__create__', type: 'individual', firstName: trimmedQuery }}
              as={Fragment}
            >
              {({ active }) => (
                <li
                  className={`relative cursor-pointer select-none px-4 py-2 text-sm transition-colors border-t ${
                    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                >
                  {queryIsPhone ? `Crear cliente sin datos · tel ${trimmedQuery}` : 'Crear cliente...'}
                </li>
              )}
            </Combobox.Option>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
